import type {
	ArtifactStorageStatus,
	AudioGenerationRequest,
	AudioGenerationResponse,
	AudioReferenceUploadResponse,
	AuthSession,
	AuthUser,
	AutoPresetResponse,
	BackgroundRemovalRequest,
	BackgroundRemovalResponse,
	CompatibilityResponse,
	ControlUploadResponse,
	ControlnetType,
	FirstImage,
	GenerationJob,
	GenerationJobKind,
	GenerationRequest,
	GenerationResponse,
	HistoryImage,
	JobEvent,
	LoRAInfo,
	LocalComponent,
	MachineLink,
	ManagedMediaUploadResponse,
	ModelInfo,
	ModelReadiness,
	MotionAdapterInfo,
	OptimizationOptions,
	OptimizationPreset,
	OptimizationProfile,
	PairingCode,
	PreflightResponse,
	RouteDecision,
	RoutePolicy,
	SchedulerInfo,
	SourceUploadResponse,
	StatusResponse,
	TimeEstimationResponse,
	TranscriptionRequest,
	TranscriptionResponse,
	TranscriptionUploadResponse,
	UpscaleRequest,
	UpscaleResponse,
	UsageSummary,
	VAEInfo,
	VideoGenerationRequest,
	VideoGenerationResponse,
	VideoUpscaleRequest,
	VideoUpscaleResponse,
	WorkerCapabilities,
} from "../types/index.js";

const AUTH_TOKEN_KEY = "image-generator.auth-token";

/** An Error that still knows which HTTP status produced it. */
export interface ApiError extends Error {
	status?: number;
}

function formatApiDetail(detail: unknown): string {
	if (typeof detail === "string") return detail;
	if (Array.isArray(detail)) {
		const messages = detail
			.map((issue) => {
				if (!issue || typeof issue !== "object") return String(issue);
				const item = issue as { loc?: unknown; msg?: unknown };
				const message = typeof item.msg === "string" ? item.msg : "Validation invalide";
				const location = Array.isArray(item.loc)
					? item.loc.filter((part) => part !== "body").join(".")
					: "";
				return location ? `${location}: ${message}` : message;
			})
			.filter(Boolean);
		return messages.join(" · ");
	}
	if (detail && typeof detail === "object") {
		return JSON.stringify(detail);
	}
	return "";
}

/** What the person reads when the site cannot be reached at all.
 *
 * `fetch` rejects with the browser's own words -- « Failed to fetch » in
 * Chrome, « Load failed » in Safari -- which reached the page as they were,
 * on a phone that had just lost its network. Status 0: no answer came back. */
export const SITE_UNREACHABLE =
	"Connexion au site impossible : vérifiez votre réseau, puis réessayez.";

async function send(input: string, init?: RequestInit): Promise<Response> {
	try {
		return await fetch(input, init);
	} catch {
		const error = new Error(SITE_UNREACHABLE) as ApiError;
		error.status = 0;
		throw error;
	}
}

/** The words for an answer that carries none of its own. */
function statusMessage(status: number): string {
	return status >= 500
		? "Le site ne répond pas pour l'instant (mise à jour en cours ?) : réessayez dans une minute."
		: `La demande a été refusée (code ${status}).`;
}

function requestHeaders(includeJson = false): HeadersInit {
	const headers: Record<string, string> = {};
	if (includeJson) headers["Content-Type"] = "application/json";
	const token = localStorage.getItem(AUTH_TOKEN_KEY);
	if (token) headers.Authorization = `Bearer ${token}`;
	return headers;
}

export const APIClient = {
	async getStatus(): Promise<StatusResponse> {
		return APIClient.get("/api/status");
	},

	/** For every catalogue model, what stands between the caller and using it. */
	async getReadiness(): Promise<ModelReadiness[]> {
		return APIClient.get<ModelReadiness[]>("/api/system/readiness");
	},

	/** The model for a first image on the caller's machine, or null when none is needed. */
	async getFirstImage(): Promise<FirstImage | null> {
		return APIClient.get<FirstImage | null>("/api/system/first-image");
	},

	async getModels(): Promise<ModelInfo[]> {
		const data = await APIClient.get<{ models: ModelInfo[] }>("/api/models");
		return data.models;
	},

	async switchModel(modelId: string, optimizationOptions?: OptimizationOptions): Promise<boolean> {
		return APIClient.postOk("/api/models/switch", {
			model_id: modelId,
			optimization_options: optimizationOptions,
		});
	},

	/** Enregistre un modèle Hugging Face ; son architecture est lue dans ses fichiers. */
	async addCustomModel(modelId: string): Promise<ModelInfo> {
		return APIClient.post<ModelInfo>("/api/models/custom", { model_id: modelId });
	},

	/** Ce qu'est un modèle Hugging Face, lu dans ses fichiers, sans rien enregistrer. */
	async inspectModel(modelId: string): Promise<ModelInfo> {
		return APIClient.post<ModelInfo>("/api/models/inspect", { model_id: modelId });
	},

	async removeCustomModel(modelId: string): Promise<void> {
		await APIClient.delete(`/api/models/custom/${encodeURIComponent(modelId)}`);
	},

	async getLoras(): Promise<LoRAInfo[]> {
		const data = await APIClient.get<{ loras: LoRAInfo[] }>("/api/loras");
		return data.loras;
	},

	async toggleLora(name: string, enabled: boolean, weight: number): Promise<void> {
		const endpoint = enabled ? "/api/loras/add" : "/api/loras/remove";
		await APIClient.post(endpoint, { name, weight });
	},

	async setLoraWeight(name: string, weight: number): Promise<void> {
		await APIClient.post("/api/loras/weight", { name, weight });
	},

	/** LoRAs and VAEs of this server's machine (local routes only). */
	async getComponents(): Promise<LocalComponent[]> {
		return APIClient.get<LocalComponent[]>("/api/components");
	},

	async getVaes(): Promise<VAEInfo[]> {
		const data = await APIClient.get<{ vaes: VAEInfo[] }>("/api/vaes");
		return data.vaes;
	},

	async setVAE(path: string | null): Promise<void> {
		await APIClient.post("/api/vaes/set", { path });
	},

	async getSchedulers(modelType: string): Promise<SchedulerInfo[]> {
		return APIClient.get(`/api/schedulers?model_type=${encodeURIComponent(modelType)}`);
	},

	async getHistory(): Promise<HistoryImage[]> {
		return APIClient.get("/api/history");
	},

	async generateImage(request: GenerationRequest): Promise<GenerationResponse> {
		return APIClient.post("/api/generate", request);
	},

	async upscaleImage(request: UpscaleRequest): Promise<UpscaleResponse> {
		return APIClient.post("/api/upscale", request);
	},

	async removeBackground(request: BackgroundRemovalRequest): Promise<BackgroundRemovalResponse> {
		return APIClient.post("/api/remove-background", request);
	},

	async generateVideo(request: VideoGenerationRequest): Promise<VideoGenerationResponse> {
		return APIClient.post("/api/video/generate", request);
	},

	async upscaleVideo(request: VideoUpscaleRequest): Promise<VideoUpscaleResponse> {
		return APIClient.post("/api/video/upscale", request);
	},

	async getAnimatediffBases(): Promise<ModelInfo[]> {
		const data = await APIClient.get<{ models: ModelInfo[] }>("/api/video/animatediff/bases");
		return data.models;
	},

	async getAnimatediffMotionAdapters(): Promise<MotionAdapterInfo[]> {
		return APIClient.get("/api/video/animatediff/motion-adapters");
	},

	async addAnimatediffMotionAdapter(adapterId: string): Promise<MotionAdapterInfo> {
		return APIClient.post<MotionAdapterInfo>("/api/video/animatediff/motion-adapters", {
			adapter_id: adapterId,
		});
	},

	async removeAnimatediffMotionAdapter(adapterId: string): Promise<void> {
		await APIClient.delete(
			`/api/video/animatediff/motion-adapters/${encodeURIComponent(adapterId)}`
		);
	},

	async uploadSource(file: File): Promise<SourceUploadResponse> {
		const res = await send("/api/media/upload-source", {
			method: "POST",
			headers: {
				"Content-Type": file.type || "application/octet-stream",
				"X-Filename": file.name,
				...requestHeaders(),
			},
			body: file,
		});
		if (!res.ok) {
			throw await APIClient.toHttpError(res);
		}
		return res.json();
	},

	async uploadVideoSource(file: File): Promise<ManagedMediaUploadResponse> {
		const res = await send("/api/media/upload-video", {
			method: "POST",
			headers: {
				"Content-Type": file.type || "application/octet-stream",
				"X-Filename": file.name,
				...requestHeaders(),
			},
			body: file,
		});
		if (!res.ok) {
			throw await APIClient.toHttpError(res);
		}
		return res.json();
	},

	async uploadAudioSource(file: File): Promise<ManagedMediaUploadResponse> {
		const res = await send("/api/media/upload-audio", {
			method: "POST",
			headers: {
				"Content-Type": file.type || "application/octet-stream",
				"X-Filename": file.name,
				...requestHeaders(),
			},
			body: file,
		});
		if (!res.ok) {
			throw await APIClient.toHttpError(res);
		}
		return res.json();
	},

	async uploadMask(file: File): Promise<SourceUploadResponse> {
		const res = await send("/api/media/upload-mask", {
			method: "POST",
			headers: {
				"Content-Type": file.type || "application/octet-stream",
				"X-Filename": file.name,
				...requestHeaders(),
			},
			body: file,
		});
		if (!res.ok) {
			throw await APIClient.toHttpError(res);
		}
		return res.json();
	},

	async uploadControlImage(
		file: File,
		controlType: ControlnetType
	): Promise<ControlUploadResponse> {
		const res = await send(
			`/api/media/upload-control-image?control_type=${encodeURIComponent(controlType)}`,
			{
				method: "POST",
				headers: {
					"Content-Type": file.type || "application/octet-stream",
					"X-Filename": file.name,
					...requestHeaders(),
				},
				body: file,
			}
		);
		if (!res.ok) {
			throw await APIClient.toHttpError(res);
		}
		return res.json();
	},

	async generateAudio(request: AudioGenerationRequest): Promise<AudioGenerationResponse> {
		return APIClient.post("/api/audio/generate", request);
	},

	async uploadAudioReference(file: File): Promise<AudioReferenceUploadResponse> {
		const res = await send("/api/audio/upload-reference", {
			method: "POST",
			headers: {
				"Content-Type": file.type || "application/octet-stream",
				"X-Filename": file.name,
				...requestHeaders(),
			},
			body: file,
		});
		if (!res.ok) {
			throw await APIClient.toHttpError(res);
		}
		return res.json();
	},

	async transcribeAudio(request: TranscriptionRequest): Promise<TranscriptionResponse> {
		return APIClient.post("/api/transcribe", request);
	},

	async uploadTranscribeSource(file: File): Promise<TranscriptionUploadResponse> {
		const res = await send("/api/upload-transcribe-source", {
			method: "POST",
			headers: {
				"Content-Type": file.type || "application/octet-stream",
				"X-Filename": file.name,
				...requestHeaders(),
			},
			body: file,
		});
		if (!res.ok) {
			throw await APIClient.toHttpError(res);
		}
		return res.json();
	},

	async register(email: string, password: string): Promise<AuthSession> {
		const session = await APIClient.post<AuthSession>("/api/auth/register", { email, password });
		APIClient.setAuthToken(session.token);
		return session;
	},

	async login(email: string, password: string): Promise<AuthSession> {
		const session = await APIClient.post<AuthSession>("/api/auth/login", { email, password });
		APIClient.setAuthToken(session.token);
		return session;
	},

	async getCurrentUser(): Promise<AuthUser> {
		return APIClient.get("/api/auth/me");
	},

	async logout(): Promise<void> {
		try {
			await APIClient.postNoContent("/api/auth/logout", undefined);
		} finally {
			APIClient.clearAuthToken();
		}
	},

	async verifyEmail(token: string): Promise<AuthUser> {
		return APIClient.post<AuthUser>("/api/auth/verify", { token });
	},

	async resendVerification(email: string): Promise<void> {
		await APIClient.post("/api/auth/resend", { email });
	},

	async requestPasswordRecovery(email: string): Promise<void> {
		await APIClient.post("/api/auth/recover", { email });
	},

	async resetPassword(token: string, password: string): Promise<void> {
		await APIClient.postNoContent("/api/auth/reset", { token, password });
	},

	setAuthToken(token: string): void {
		localStorage.setItem(AUTH_TOKEN_KEY, token);
	},

	clearAuthToken(): void {
		localStorage.removeItem(AUTH_TOKEN_KEY);
	},

	hasAuthToken(): boolean {
		return Boolean(localStorage.getItem(AUTH_TOKEN_KEY));
	},

	async listJobs(limit = 100): Promise<GenerationJob[]> {
		return APIClient.get(`/api/jobs?limit=${Math.min(Math.max(limit, 1), 500)}`);
	},

	async submitHostedJob(
		payload: Record<string, unknown>,
		routing: RoutePolicy,
		kind: GenerationJobKind = "image"
	): Promise<GenerationJob> {
		const idempotencyKey =
			typeof crypto.randomUUID === "function"
				? crypto.randomUUID()
				: `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
		return APIClient.post("/api/jobs", {
			kind,
			payload,
			idempotency_key: idempotencyKey,
			routing,
		});
	},

	async estimateHostedJob(
		kind: "image" | "video",
		payload: Record<string, unknown>,
		routing: RoutePolicy
	): Promise<RouteDecision> {
		const idempotencyKey =
			typeof crypto.randomUUID === "function"
				? crypto.randomUUID()
				: `estimate-${Date.now()}-${Math.random().toString(36).slice(2)}`;
		return APIClient.post("/api/jobs/estimate", {
			kind,
			payload,
			idempotency_key: idempotencyKey,
			routing,
		});
	},

	async getJob(jobId: string): Promise<GenerationJob> {
		return APIClient.get(`/api/jobs/${encodeURIComponent(jobId)}`);
	},

	async getJobEvents(jobId: string): Promise<JobEvent[]> {
		return APIClient.get(`/api/jobs/${encodeURIComponent(jobId)}/events`);
	},

	async getJobEventPage(
		jobId: string,
		after = 0,
		limit = 200
	): Promise<{ events: JobEvent[]; next_cursor: number }> {
		return APIClient.get(
			`/api/jobs/${encodeURIComponent(jobId)}/events/page?after=${Math.max(
				0,
				Math.trunc(after)
			)}&limit=${Math.min(Math.max(Math.trunc(limit), 1), 500)}`
		);
	},

	async cancelJob(jobId: string): Promise<GenerationJob> {
		return APIClient.post(`/api/jobs/${encodeURIComponent(jobId)}/cancel`, undefined);
	},

	async getUsageSummary(since?: string): Promise<UsageSummary> {
		const query = since ? `?since=${encodeURIComponent(since)}` : "";
		return APIClient.get(`/api/jobs/usage${query}`);
	},

	async listWorkers(): Promise<WorkerCapabilities[]> {
		return APIClient.get("/api/agent/workers");
	},

	/** Retire une machine de la liste du compte (elle revient si elle se reconnecte). */
	async forgetWorker(workerId: string): Promise<void> {
		await APIClient.delete(`/api/agent/workers/${encodeURIComponent(workerId)}`);
	},

	async getSharedComputeStatus(): Promise<ArtifactStorageStatus> {
		return APIClient.get("/api/agent/shared-status");
	},

	/** A computer asking to be linked (the program opened `/?relier=<id>`). */
	async getMachineLink(linkId: string): Promise<MachineLink> {
		return APIClient.get(`/api/agent/link/${encodeURIComponent(linkId)}`);
	},

	async answerMachineLink(linkId: string, approve: boolean): Promise<MachineLink> {
		const decision = approve ? "approve" : "deny";
		return APIClient.post(`/api/agent/link/${encodeURIComponent(linkId)}/${decision}`, undefined);
	},

	async createPairingCode(): Promise<PairingCode> {
		return APIClient.post("/api/agent/pairing-code", undefined);
	},

	async estimateTime(
		width: number,
		height: number,
		steps: number,
		scheduler = "euler_a",
		optimization_options?: OptimizationOptions,
		architecture?: string,
		has_controlnet?: boolean
	): Promise<TimeEstimationResponse> {
		try {
			return await APIClient.post("/api/estimate-time", {
				width,
				height,
				steps,
				scheduler,
				optimization_options,
				architecture,
				has_controlnet,
			});
		} catch (error) {
			console.warn("Estimation failed", error);
			return { estimated_seconds: 0, message: "..." } as unknown as TimeEstimationResponse;
		}
	},

	async preflight(
		modelId: string,
		width: number,
		height: number,
		steps: number,
		batchSize = 1,
		optimization_options?: OptimizationOptions,
		architecture?: string
	): Promise<PreflightResponse> {
		return APIClient.post("/api/system/preflight", {
			model_id: modelId,
			width,
			height,
			steps,
			batch_size: batchSize,
			optimization_options,
			architecture,
		});
	},

	async getAllOptimizationPresets(): Promise<Record<string, OptimizationPreset>> {
		return APIClient.get("/api/optimizations/presets");
	},

	async getAvailableOptimizations(): Promise<unknown[]> {
		return APIClient.get("/api/optimizations/available");
	},

	async getOptimizationProfiles(): Promise<OptimizationProfile[]> {
		return APIClient.get("/api/optimizations/profiles");
	},

	async saveOptimizationProfile(
		name: string,
		options: OptimizationOptions
	): Promise<OptimizationProfile> {
		return APIClient.put(`/api/optimizations/profiles/${encodeURIComponent(name)}`, options);
	},

	async deleteOptimizationProfile(name: string): Promise<void> {
		await APIClient.delete(`/api/optimizations/profiles/${encodeURIComponent(name)}`);
	},

	/**
	 * Get optimal settings based on hardware analysis.
	 * @param architecture - Target model architecture (sd15, sdxl, sd3, flux)
	 */
	async getAutoPreset(architecture = "sd15", profile = "balanced"): Promise<AutoPresetResponse> {
		return APIClient.get(
			`/api/optimizations/auto-preset?architecture=${architecture}&profile=${profile}`
		);
	},

	/**
	 * Check hardware compatibility with an architecture.
	 * @param architecture - Target model architecture
	 */
	async getCompatibilityInfo(architecture = "sd15"): Promise<CompatibilityResponse> {
		return APIClient.get(`/api/optimizations/compatibility?architecture=${architecture}`);
	},

	// ==================== Internal Helpers ====================

	async get<T>(url: string): Promise<T> {
		const res = await send(url, { headers: requestHeaders() });
		if (!res.ok) {
			throw await APIClient.toHttpError(res);
		}
		return res.json();
	},

	async put<T>(url: string, body: unknown): Promise<T> {
		const res = await send(url, {
			method: "PUT",
			headers: requestHeaders(true),
			body: JSON.stringify(body),
		});
		if (!res.ok) {
			throw await APIClient.toHttpError(res);
		}
		return res.json();
	},

	async delete(url: string): Promise<void> {
		const res = await send(url, { method: "DELETE", headers: requestHeaders() });
		if (!res.ok) {
			throw await APIClient.toHttpError(res);
		}
	},

	async post<T>(url: string, body: unknown): Promise<T> {
		const res = await send(url, {
			method: "POST",
			headers: requestHeaders(true),
			body: body === undefined ? undefined : JSON.stringify(body),
		});
		if (!res.ok) {
			throw await APIClient.toHttpError(res);
		}
		return res.json();
	},

	async postOk(url: string, body: unknown): Promise<boolean> {
		const res = await send(url, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(body),
		});
		return res.ok;
	},

	async postNoContent(url: string, body: unknown): Promise<void> {
		const res = await send(url, {
			method: "POST",
			headers: requestHeaders(true),
			body: body === undefined ? undefined : JSON.stringify(body),
		});
		if (!res.ok) {
			throw await APIClient.toHttpError(res);
		}
	},

	async toHttpError(res: Response): Promise<ApiError> {
		let detail = "";
		try {
			const payload = (await res.json()) as { detail?: unknown; error?: unknown };
			detail = formatApiDetail(payload.detail) || formatApiDetail(payload.error);
		} catch {
			// The server may return an empty or non-JSON error response.
		}
		// A detail the server wrote is already a sentence for the person
		// reading it; "HTTP 409:" in front of it is noise, and this text is the
		// first thing a new account sees when no machine is attached yet. With
		// no detail there is nothing to show but the status.
		//
		// The code rides on the error rather than inside its message: callers
		// used to sniff for the string "HTTP 401" to spot an expired session,
		// which would have broken the moment the message stopped carrying it.
		const error = new Error(detail || statusMessage(res.status)) as ApiError;
		error.status = res.status;
		return error;
	},
};
